import { useEffect } from "react";
import { useAppDispatch } from "../../store";
import { setLocalStorageItem } from "../../reducers/loginLocalStorage";
import { getDisplayMode } from "../../components/Authentication/Token";
import { loadSiteTools } from "../util/siteTools";
import { tealiumTracking } from "../util/routeUtils";
import type { BootType } from "../../reducers/boot";
import { logger } from "../util/logger";

type UseAppInitializationParams = {
  boot: BootType;
  pathname: string;
};

/**
 * Hook that handles one-time startup tasks:
 * - Sets display mode from query params
 * - Initializes boot state for authenticated users
 * - Loads site tools when ready
 *
 * @param boot - The boot state from Redux (pass from parent to ensure consistent reference)
 * @param pathname - The current pathname
 */
export const useAppInitialization = ({ boot, pathname }: UseAppInitializationParams) => {
  const dispatch = useAppDispatch();

  // Determine if this is a new ABO login (not on tealium tracking routes and has ABO)
  const isNewAboLogin = !tealiumTracking(pathname) && !!boot.user.abo;

  // Set the display mode based on the query parameters
  useEffect(() => {
    const displayMode = getDisplayMode();
    logger.info("useAppInitialization: Setting display mode", displayMode);

    const update = {
      headless: displayMode.headless || sessionStorage.getItem("headless") === "true",
      footless: displayMode.footless,
    };

    if (displayMode.headless) {
      sessionStorage.setItem("headless", "true");
    }

    dispatch(setLocalStorageItem(update));
  }, [dispatch]);

  // Load site tools when ready for authenticated users
  useEffect(() => {
    if (isNewAboLogin && !boot.isShellLoading && !boot.isShellFetching) {
      loadSiteTools(boot);
    }
  }, [isNewAboLogin, boot]);

  return { isNewAboLogin };
};
